import * as React from 'react';
import { useState, useEffect } from 'react';

import { SchoolInfoFormProps, SchoolOption } from './types';

import SchoolNameAutocomplete from './SchoolNameAutocomplete';

/**
 * 学校情報フォーム全体のコンポーネント
 * 新デザイン（new.html）に完全一致させたマークアップ
 */
const SchoolInfoForm: React.FC<SchoolInfoFormProps> = ({
  currentStatus: initialCurrentStatus = '',
  schoolType: initialSchoolType = '',
  schoolMasterId: initialSchoolMasterId,
  schoolName: initialSchoolName = '',
  schoolNameFree: initialSchoolNameFree = '',
  selectedSchool: initialSelectedSchool = null,
  majorSubjectId: initialMajorSubjectId,
  graduationYear: initialGraduationYear,
  majorSubjects = [],
  onCurrentStatusChange,
  onSchoolTypeChange,
  onSchoolMasterChange,
  onSchoolNameFreeChange,
  onMajorSubjectChange,
  onGraduationYearChange,
  canEditSchoolInfo = true,
  forceEnrolled = false,
  errors = {}
}) => {
  // 内部状態管理
  const [currentStatus, setCurrentStatus] = useState<string>(forceEnrolled ? '1' : initialCurrentStatus);
  const [schoolType, setSchoolType] = useState<string>(initialSchoolType);
  const [schoolMasterId, setSchoolMasterId] = useState<number | undefined>(initialSchoolMasterId);
  const [schoolName, setSchoolName] = useState<string>(initialSchoolName);
  const [schoolNameFree, setSchoolNameFree] = useState<string>(initialSchoolNameFree);
  const [graduationYear, setGraduationYear] = useState<number | undefined>(initialGraduationYear);
  const [selectedMajorSubject, setSelectedMajorSubject] = useState<number | undefined>(initialMajorSubjectId);

  // 選択中の学校（react-select用）- バリデーションエラー時の復元用に初期値を設定
  const [selectedSchool, setSelectedSchool] = useState<SchoolOption | null>(initialSelectedSchool);

  /**
   * 在学中かどうか
   */
  const isEnrolled = currentStatus === '1';

  /**
   * 予測入力対象の学校種別か
   * 大学・大学院(1), 短期大学(2), 農業大学校(3)
   */
  const isPredictableSchoolType = ['1', '2', '3'].includes(schoolType);

  /**
   * 選択中の専攻科目オブジェクトを取得
   */
  const selectedMajorSubjectObj = majorSubjects.find((ms) => ms.id === selectedMajorSubject);

  /**
   * トップレベルの専攻科目（親カテゴリなし）
   */
  const topLevelMajorSubjects = majorSubjects.filter((ms) => !ms.parent_category);

  /**
   * その他専攻科目の選択肢（専攻科目が「その他」の場合のみ）
   */
  const otherMajorSubjects = majorSubjects.filter((ms) => ms.category === 'other' && ms.parent_category);

  /**
   * 「その他」（親カテゴリ）のID
   */
  const otherParentId = majorSubjects.find((ms) => ms.category === 'other' && !ms.parent_category)?.id;

  /**
   * 選択中の専攻科目がサブカテゴリか（親の「その他」の子項目か）
   */
  const isSubCategory = selectedMajorSubjectObj?.category === 'other' && selectedMajorSubjectObj?.parent_category;

  /**
   * 専攻科目が「その他」（親カテゴリ）か
   */
  const isMajorSubjectOther =
    selectedMajorSubjectObj?.category === 'other' && !selectedMajorSubjectObj?.parent_category;

  /**
   * 親項目プルダウンの表示値
   * サブカテゴリが選択されている場合は、親の「その他」を表示
   */
  const topLevelValue = isSubCategory ? otherParentId : selectedMajorSubject;

  /**
   * propsの変更を監視して状態を更新（戻るボタン対応）
   */
  useEffect(() => {
    if (!forceEnrolled) setCurrentStatus(initialCurrentStatus);
    setSchoolType(initialSchoolType);
    setSchoolMasterId(initialSchoolMasterId);
    setSchoolName(initialSchoolName);
    setSchoolNameFree(initialSchoolNameFree);
    setGraduationYear(initialGraduationYear);
    setSelectedMajorSubject(initialMajorSubjectId);
    setSelectedSchool(initialSelectedSchool);
  }, [
    initialCurrentStatus,
    initialSchoolType,
    initialSchoolMasterId,
    initialSchoolName,
    initialSchoolNameFree,
    initialGraduationYear,
    initialMajorSubjectId,
    initialSelectedSchool
  ]);

  /**
   * hidden fieldsへの値の同期
   */
  useEffect(() => {
    if (typeof window !== 'undefined' && window.updateHiddenFields) {
      window.updateHiddenFields({
        currentStatus: currentStatus,
        schoolType: schoolType,
        schoolMasterId: schoolMasterId,
        schoolName: schoolName,
        schoolNameFree: schoolNameFree,
        majorSubjectId: selectedMajorSubject,
        graduationYear: graduationYear
      });
    }
  }, [currentStatus, schoolType, schoolMasterId, schoolName, schoolNameFree, selectedMajorSubject, graduationYear]);

  /**
   * 現在のご状況変更ハンドラー
   */
  const handleCurrentStatusChange = (value: string) => {
    setCurrentStatus(value);
    onCurrentStatusChange?.(value);
  };

  /**
   * 学校種別変更ハンドラー
   */
  const handleSchoolTypeChange = (value: string) => {
    setSchoolType(value);
    setSchoolMasterId(undefined);
    setSchoolName('');
    setSchoolNameFree('');
    setSelectedSchool(null);
    onSchoolTypeChange?.(value);
  };

  /**
   * 学校選択時のハンドラー（予測入力）
   */
  const handleSchoolChange = (option: SchoolOption | null) => {
    setSelectedSchool(option);
    if (option) {
      setSchoolMasterId(option.value);
      setSchoolName(option.label);
      onSchoolMasterChange?.(option.value, option.label);
    } else {
      setSchoolMasterId(undefined);
      setSchoolName('');
      onSchoolMasterChange?.(0, '');
    }
  };

  /**
   * 学校名変更ハンドラー（テキスト入力）
   */
  const handleSchoolNameFreeChange = (value: string) => {
    setSchoolNameFree(value);
    onSchoolNameFreeChange?.(value);
  };

  /**
   * 専攻科目選択時のハンドラー（トップレベル）
   */
  const handleMajorSubjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    const subject = majorSubjects.find((ms) => ms.id === id);

    // 「その他」（親カテゴリ）を選んだ場合でも、IDは保持する（詳細プルダウン表示のため）
    // ただし、hidden fieldには詳細選択待ちとして 0 を送る
    setSelectedMajorSubject(id);

    if (subject && subject.category === 'other' && !subject.parent_category) {
      onMajorSubjectChange?.(0); // 詳細選択待ちとして未選択を通知
    } else {
      onMajorSubjectChange?.(id);
    }
  };

  /**
   * 専攻科目詳細選択時のハンドラー（サブレベル）
   */
  const handleMajorSubjectDetailChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    setSelectedMajorSubject(id);
    onMajorSubjectChange?.(id);
  };

  /**
   * 卒業年度変更ハンドラー
   */
  const handleGraduationYearChange = (year: number) => {
    setGraduationYear(year);
    onGraduationYearChange?.(year);
  };

  /**
   * 卒業年度の選択肢を生成（2025年基準で固定）
   */
  const graduationYearOptions = (() => {
    const baseYear = 2025; // 2025年時点の選択肢を固定
    const years = [];
    for (let i = 1; i <= 6; i++) {
      const year = baseYear + i;
      years.push({ value: year, label: `${year}年3月卒 (${String(year).slice(-2)}卒)` });
    }
    return years;
  })();

  return (
    <>
      {/* 現在のご状況 - 完全なdl要素として出力 */}
      <dl style={{ marginTop: '3rem', marginBottom: isEnrolled ? '0' : '3rem' }}>
        <dt>
          <label htmlFor="stutas">
            <span className="required">必須</span>現在のご状況
          </label>
        </dt>
        <dd>
          {forceEnrolled ? (
            <p>在学中（学生の方）</p>
          ) : (
            <div>
              <select
                id="stutas"
                name="registration_status"
                value={currentStatus}
                onChange={(e) => handleCurrentStatusChange(e.target.value)}
                disabled={!canEditSchoolInfo}
                className={errors.currentStatus && errors.currentStatus.length > 0 ? 'select-s ipt-error' : 'select-s'}
              >
                <option value="">在学/就業状況を選択</option>
                <option value="1">在学中（学生の方）</option>
                <option value="2">在職中</option>
                <option value="3">離職中</option>
                <option value="4">その他</option>
              </select>
              {errors.currentStatus && errors.currentStatus.length > 0 && (
                <p className="p-error">{errors.currentStatus[0]}</p>
              )}
            </div>
          )}
        </dd>
      </dl>

      {/* 在学状況の詳細（在学中の場合のみ表示） - 別のdl要素として出力 */}
      {isEnrolled && (
        <dl style={{ marginTop: '3rem', marginBottom: '3rem' }}>
          <dt>
            <label htmlFor="school-type">
              <span className="required">必須</span>在学状況の詳細
            </label>
          </dt>
          <dd className="form-academic">
            {/* 学校種別 */}
            <div>
              <p className="note">学校種別</p>
              <select
                id="school-type"
                name="school_type"
                value={schoolType}
                onChange={(e) => handleSchoolTypeChange(e.target.value)}
                disabled={!canEditSchoolInfo}
                className={errors.schoolType && errors.schoolType.length > 0 ? 'select-s ipt-error' : 'select-s'}
              >
                <option value="">種別を選択</option>
                <option value="1">大学・大学院</option>
                <option value="2">短期大学</option>
                <option value="3">農業大学校</option>
                <option value="4">専門学校</option>
                <option value="5">高等学校・高専</option>
                <option value="6">その他</option>
              </select>
              {errors.schoolType && errors.schoolType.length > 0 && <p className="p-error">{errors.schoolType[0]}</p>}
            </div>

            {/* 学校名（条件分岐） */}
            {schoolType && (
              <>
                {!isPredictableSchoolType ? (
                  // テキスト入力（高等学校・高専/専門学校/その他）
                  <div>
                    <p className="note">学校名</p>
                    <input
                      type="text"
                      id="school_name_free"
                      name="school_name_free"
                      value={schoolNameFree || ''}
                      onChange={(e) => handleSchoolNameFreeChange(e.target.value)}
                      placeholder="例) 〇〇高等学校"
                      disabled={!canEditSchoolInfo}
                      className={errors.schoolName && errors.schoolName.length > 0 ? 'ipt-m ipt-error' : 'ipt-m'}
                      maxLength={255}
                    />
                    {errors.schoolName && errors.schoolName.length > 0 && (
                      <p className="p-error">{errors.schoolName[0]}</p>
                    )}
                  </div>
                ) : (
                  // 予測入力（大学・大学院/短期大学/農業大学校）
                  <div>
                    <p className="note">学校名 (※入力すると候補が表示されます)</p>
                    <SchoolNameAutocomplete
                      schoolType={schoolType}
                      value={selectedSchool}
                      onChange={handleSchoolChange}
                      isDisabled={!canEditSchoolInfo}
                      hasError={errors.schoolName && errors.schoolName.length > 0}
                    />
                    {errors.schoolName && errors.schoolName.length > 0 && (
                      <p className="p-error">{errors.schoolName[0]}</p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* 専攻科目（予測入力対象の学校種別の場合のみ） */}
            {isPredictableSchoolType && (
              <div>
                <p className="note">専攻科目</p>
                <select
                  id="major_subject_id"
                  name="major_subject_id"
                  value={topLevelValue || ''}
                  onChange={handleMajorSubjectChange}
                  disabled={!canEditSchoolInfo}
                  className={
                    errors.majorSubjectId && errors.majorSubjectId.length > 0 ? 'select-m ipt-error' : 'select-m'
                  }
                >
                  <option value="">専攻科目を選択</option>
                  {topLevelMajorSubjects.map((ms) => (
                    <option key={ms.id} value={ms.id}>
                      {ms.displayName}
                    </option>
                  ))}
                </select>
                {errors.majorSubjectId && errors.majorSubjectId.length > 0 && (
                  <p className="p-error">{errors.majorSubjectId[0]}</p>
                )}
              </div>
            )}

            {/* 専攻科目詳細（専攻科目が「その他」の場合のみ） */}
            {isPredictableSchoolType && (isMajorSubjectOther || isSubCategory) && (
              <div>
                <select
                  id="major_subject_detail_id"
                  name="major_subject_detail_id"
                  value={isSubCategory ? selectedMajorSubject || '' : ''}
                  onChange={handleMajorSubjectDetailChange}
                  disabled={!canEditSchoolInfo}
                  className="select-m"
                >
                  <option value="">専攻科目を選択</option>
                  {otherMajorSubjects.map((ms) => (
                    <option key={ms.id} value={ms.id}>
                      {ms.displayName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* 卒業見込み */}
            <div>
              <p className="note">卒業見込み</p>
              <select
                id="graduation_year"
                name="graduation_year"
                value={graduationYear || ''}
                onChange={(e) => handleGraduationYearChange(Number(e.target.value))}
                disabled={!canEditSchoolInfo}
                className={
                  errors.graduationYear && errors.graduationYear.length > 0 ? 'select-s ipt-error' : 'select-s'
                }
              >
                <option value="">選択してください</option>
                {graduationYearOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="note">※卒業見込みが9月の場合は、同年度の「◯年3月卒」を選択してください。</p>
              {errors.graduationYear && errors.graduationYear.length > 0 && (
                <p className="p-error">{errors.graduationYear[0]}</p>
              )}
            </div>
          </dd>
        </dl>
      )}
    </>
  );
};

export default SchoolInfoForm;
